import { ScreenHeader } from "@/components/common/ScreenHeader";
import { DesktopAppFrame } from "@/components/desktop/DesktopChrome";
import { ThemedText } from "@/components/themed-text";
import { Loader } from "@/components/ui/loader";
import InfoRow from "@/components/vehicle/info-row";
import TagSection from "@/components/vehicle/tag-section";
import { BrandColors, Colors } from "@/constants/theme";
import { getVehicleTypeLabel } from "@/data/vehicle";
import {
  useUpdateAvailability,
  useUploadVehicleDocument,
  useVehicle,
} from "@/hooks/queries/use-vehicles-query";
import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import {
  imagePickerPresets,
  normalizeImageAsset,
  pickImageFromLibrary,
} from "@/lib/image-picker";
import { showToast } from "@/lib/toast";
import { EQUIPMENT_OPTIONS, SERVICES_OPTIONS } from "@/lib/validation/vehicle";
import Ionicons from "@react-native-vector-icons/ionicons";
import { Stack, useLocalSearchParams, useRouter } from "expo-router";
import React from "react";
import {
  Linking,
  RefreshControl,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
  useColorScheme,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatMileage = (mileage?: string) => {
  if (!mileage) return null;
  const numeric = mileage.replace(/,/g, "");
  if (!/^\d+$/.test(numeric)) return mileage;
  return Number(numeric).toLocaleString();
};

export default function VehicleDetailsScreen() {
  const { id } = useLocalSearchParams();
  const desktop = useDesktopLayout();
  const colorScheme = useColorScheme();
  const theme = Colors[colorScheme ?? "light"];
  const router = useRouter();
  const {
    data: vehicle,
    isLoading,
    refetch,
    isRefetching,
  } = useVehicle(Number(id));
  const updateAvailability = useUpdateAvailability();
  const uploadDocument = useUploadVehicleDocument();

  const handleToggleAvailability = async () => {
    if (!vehicle) return;

    updateAvailability.mutate(
      {
        id: vehicle.id,
        is_available: !vehicle.is_available,
      },
      {
        onSuccess: (updated) => {
          showToast.success(
            `Vehicle marked as ${updated.is_available ? "available" : "unavailable"}`,
          );
        },
        onError: (error: any) => {
          showToast.error(
            error.response?.data?.message || "Failed to update availability",
          );
        },
      },
    );
  };

  const handleReupload = async (type: string) => {
    const asset = await pickImageFromLibrary(imagePickerPresets.document);
    if (!asset) {
      return;
    }

    const normalizedAsset = await normalizeImageAsset(asset);
    uploadDocument.mutate(
      {
        id: Number(id),
        type,
        asset: normalizedAsset,
      },
      {
        onSuccess: () => {
          showToast.success("Document re-uploaded successfully");
        },
        onError: (error: any) => {
          showToast.error(
            error.response?.data?.message || "Failed to upload document",
          );
        },
      },
    );
  };

  const goToEdit = () => {
    if (!vehicle) return;
    router.push({
      pathname: `/vehicles/${vehicle.id}/edit` as any,
      params: { vehicle: JSON.stringify(vehicle) },
    });
  };

  const serviceLabels =
    vehicle?.services_enabled?.map((serviceValue) => {
      const service = SERVICES_OPTIONS.find((s) => s.value === serviceValue);
      return service?.label || serviceValue;
    }) ?? [];

  const equipmentLabels =
    vehicle?.equipment?.map((equipmentValue) => {
      const equipment = EQUIPMENT_OPTIONS.find(
        (e) => e.value === equipmentValue,
      );
      return equipment?.label || equipmentValue;
    }) ?? [];

  const infoRows = vehicle
    ? [
        { label: "Registration Number", value: vehicle.registration_number },
        vehicle.vin ? { label: "VIN last 5 digits", value: vehicle.vin } : null,
        {
          label: "Vehicle Type",
          value: getVehicleTypeLabel(vehicle.vehicle_type),
        },
        vehicle.make
          ? { label: "Make", value: vehicle.make.toUpperCase() }
          : null,
        vehicle.model
          ? { label: "Model", value: vehicle.model.toUpperCase() }
          : null,
        vehicle.mileage
          ? {
              label: "Mileage",
              value: formatMileage(vehicle.mileage) ?? vehicle.mileage,
            }
          : null,
        vehicle.year ? { label: "Year", value: String(vehicle.year) } : null,
        {
          label: "Registered On",
          value: formatDate(vehicle.created_at),
        },
      ].filter(Boolean)
    : [];

  return (
    <DesktopAppFrame>
      <SafeAreaView
        style={[styles.safeArea, { backgroundColor: theme.background }]}
        edges={[]}
      >
        <Stack.Screen
          options={{
            title: vehicle?.registration_number ?? "Vehicle Details",
            headerShown: !desktop,
            headerStyle: {
              backgroundColor: theme.background,
            },
            headerTintColor: theme.text,
            headerBackTitle: "Back",
          }}
        />
        {desktop && (
          <ScreenHeader
            title={vehicle?.registration_number ?? "Vehicle Details"}
            showMenu={false}
          />
        )}
        {isLoading || !vehicle ? (
          <Loader />
        ) : (
          <ScrollView
            style={styles.container}
            contentContainerStyle={[
              styles.scrollContent,
              desktop && styles.desktopContent,
            ]}
            refreshControl={
              <RefreshControl refreshing={isRefetching} onRefresh={refetch} />
            }
          >
            {!vehicle.is_verified ? (
              <View style={styles.reviewBanner}>
                <Ionicons name="time-outline" size={22} color="#EA580C" />
                <ThemedText style={styles.reviewBannerText} type="small">
                  This vehicle is currently under review. You will be able to
                  use it for bookings once it is verified.
                </ThemedText>
              </View>
            ) : null}

            <View style={styles.statusRow}>
              {vehicle.is_verified && (
                <>
                  <View style={styles.verifiedBadge}>
                    <Ionicons
                      name="checkmark-circle"
                      size={16}
                      color={BrandColors.primary}
                    />
                    <ThemedText
                      style={styles.verifiedBadgeText}
                      type="smallSemiBold"
                    >
                      Verified
                    </ThemedText>
                  </View>
                  <View
                    style={[
                      styles.availabilityBadge,
                      vehicle.is_available
                        ? styles.availableBadge
                        : styles.unavailableBadge,
                    ]}
                  >
                    <Ionicons
                      name={
                        vehicle.is_available
                          ? "checkmark-circle"
                          : "ban-outline"
                      }
                      size={16}
                      color={
                        vehicle.is_available
                          ? BrandColors.primary
                          : BrandColors.mediumGray
                      }
                    />
                    <ThemedText
                      style={[
                        styles.availabilityBadgeText,
                        vehicle.is_available
                          ? styles.availableText
                          : styles.unavailableText,
                      ]}
                      type="smallSemiBold"
                    >
                      {vehicle.is_available ? "Available" : "Unavailable"}
                    </ThemedText>
                  </View>
                </>
              )}
            </View>

            <View style={styles.section}>
              <ThemedText style={styles.cardTitle} type="defaultSemiBold">
                Vehicle Information
              </ThemedText>
              <View
                style={[
                  styles.infoCard,
                  {
                    backgroundColor: theme.background,
                    borderColor: theme.border,
                  },
                ]}
              >
                {infoRows.map((row, index) => (
                  <InfoRow
                    key={row!.label}
                    label={row!.label}
                    value={row!.value}
                    isLast={index === infoRows.length - 1}
                  />
                ))}
              </View>
            </View>

            {serviceLabels.length > 0 ? (
              <TagSection
                title="Offered Services"
                items={serviceLabels}
                onEdit={vehicle.is_verified ? goToEdit : undefined}
              />
            ) : null}

            {equipmentLabels.length > 0 ? (
              <TagSection
                title="Vehicle Equipment"
                items={equipmentLabels}
                onEdit={vehicle.is_verified ? goToEdit : undefined}
              />
            ) : null}

            {vehicle.documents && vehicle.documents.length > 0 ? (
              <View style={styles.section}>
                <ThemedText style={styles.cardTitle} type="defaultSemiBold">
                  Documents
                </ThemedText>
                <View
                  style={[
                    styles.infoCard,
                    {
                      borderColor: theme.border,
                      backgroundColor: theme.background,
                    },
                  ]}
                >
                  {vehicle.documents.map((doc, index) => (
                    <View
                      key={doc.id}
                      style={[
                        styles.documentItem,
                        { borderBottomColor: theme.border },
                        index === vehicle.documents!.length - 1 && {
                          borderBottomWidth: 0,
                          paddingBottom: 0,
                        },
                      ]}
                    >
                      <View style={styles.documentHeader}>
                        <View style={styles.documentInfo}>
                          <Ionicons
                            name={
                              doc.type === "vehicle_photo"
                                ? "image-outline"
                                : doc.type === "insurance"
                                  ? "shield-checkmark-outline"
                                  : "card-outline"
                            }
                            size={22}
                            color={BrandColors.mediumGray}
                          />
                          <View style={styles.documentCopy}>
                            <ThemedText
                              type="smallSemiBold"
                            >
                              {doc.type.replace("_", " ").toUpperCase()}
                            </ThemedText>
                            <ThemedText
                              style={styles.documentDate}
                              type="extraSmall"
                            >
                              Uploaded on {formatDate(doc.created_at)}
                            </ThemedText>
                            <TouchableOpacity
                              onPress={() => Linking.openURL(doc.url)}
                            >
                              <ThemedText
                                style={styles.viewLinkText}
                                type="extraSmallSemiBold"
                              >
                                View Document
                              </ThemedText>
                            </TouchableOpacity>
                          </View>
                        </View>
                        <View
                          style={[
                            styles.docStatusBadge,
                            doc.is_verified
                              ? styles.docVerifiedBadge
                              : styles.docPendingBadge,
                          ]}
                        >
                          <ThemedText
                            style={[
                              styles.docStatusText,
                              doc.is_verified
                                ? styles.docVerifiedText
                                : styles.docPendingText,
                            ]}
                            type="extraSmallSemiBold"
                          >
                            {doc.is_verified ? "Verified" : "Pending"}
                          </ThemedText>
                        </View>
                      </View>

                      {!doc.is_verified && doc.rejection_reason ? (
                        <View style={styles.rejectionContainer}>
                          <Ionicons
                            name="alert-circle-outline"
                            size={16}
                            color={BrandColors.error}
                          />
                          <ThemedText
                            style={styles.rejectionText}
                            type="extraSmall"
                          >
                            Rejected: {doc.rejection_reason}
                          </ThemedText>
                          <TouchableOpacity
                            onPress={() => handleReupload(doc.type)}
                            style={styles.reuploadButton}
                          >
                            <ThemedText
                              style={styles.reuploadButtonText}
                              type="extraSmallSemiBold"
                            >
                              Re-upload
                            </ThemedText>
                          </TouchableOpacity>
                        </View>
                      ) : null}
                    </View>
                  ))}
                </View>
              </View>
            ) : null}

            <View style={styles.footerActions}>
              {vehicle.is_verified && (
                <>
                  <TouchableOpacity
                    style={styles.primaryButton}
                    onPress={handleToggleAvailability}
                    activeOpacity={0.9}
                  >
                    <Ionicons
                      name={
                        vehicle.is_available
                          ? "ban-outline"
                          : "checkmark-circle-outline"
                      }
                      size={18}
                      color={BrandColors.white}
                    />
                    <ThemedText
                      style={styles.primaryButtonText}
                      type="smallSemiBold"
                    >
                      Mark as{" "}
                      {vehicle.is_available ? "Unavailable" : "Available"}
                    </ThemedText>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.secondaryButton}
                    onPress={goToEdit}
                    activeOpacity={0.9}
                  >
                    <Ionicons
                      name="pencil-outline"
                      size={18}
                      color={BrandColors.charcoal}
                    />
                    <ThemedText
                      style={styles.secondaryButtonText}
                      type="smallSemiBold"
                    >
                      Edit Vehicle
                    </ThemedText>
                  </TouchableOpacity>
                </>
              )}
            </View>
          </ScrollView>
        )}
      </SafeAreaView>
    </DesktopAppFrame>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  container: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 32,
  },
  desktopContent: {
    width: "100%",
    maxWidth: 720,
    alignSelf: "center",
  },
  reviewBanner: {
    marginHorizontal: 16,
    marginTop: 16,
    padding: 14,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    backgroundColor: "#FFF7ED",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#FED7AA",
  },
  reviewBannerText: {
    flex: 1,
    color: "#C2410C",
    lineHeight: 20,
  },
  statusRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
    paddingHorizontal: 16,
    paddingTop: 16,
  },
  verifiedBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: BrandColors.primaryLight,
    borderRadius: 20,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  verifiedBadgeText: {
    color: BrandColors.primary,
    fontWeight: "600",
  },
  availabilityBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    borderRadius: 20,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  availableBadge: {
    backgroundColor: BrandColors.primaryLight,
  },
  unavailableBadge: {
    backgroundColor: "#F3F4F6",
  },
  availabilityBadgeText: {
    fontWeight: "600",
  },
  availableText: {
    color: BrandColors.primary,
  },
  unavailableText: {
    color: BrandColors.mediumGray,
  },
  section: {
    paddingHorizontal: 16,
    paddingTop: 20,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: "700",
  },
  infoCard: {
    borderRadius: 16,
    paddingHorizontal: 16,
    paddingVertical: 4,
    borderWidth: 1,
    borderColor: BrandColors.mediumGray,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
    elevation: 2,
  },
  documentItem: {
    paddingVertical: 14,
    borderBottomWidth: 1,
  },
  documentHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
  },
  documentInfo: {
    flex: 1,
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
  },
  documentCopy: {
    flex: 1,
    gap: 2,
  },
  documentType: {
    color: BrandColors.charcoal,
  },
  documentDate: {
    color: BrandColors.mediumGray,
  },
  docStatusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  docVerifiedBadge: {
    backgroundColor: BrandColors.primaryLight,
  },
  docPendingBadge: {
    backgroundColor: "#FFF7ED",
  },
  docStatusText: {
    fontWeight: "600",
  },
  docVerifiedText: {
    color: BrandColors.primary,
  },
  docPendingText: {
    color: "#EA580C",
  },
  rejectionContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginTop: 12,
    padding: 10,
    backgroundColor: "#FEF2F2",
    borderRadius: 10,
  },
  rejectionText: {
    flex: 1,
    color: BrandColors.error,
  },
  reuploadButton: {
    backgroundColor: BrandColors.error,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
  },
  reuploadButtonText: {
    color: BrandColors.white,
  },
  viewLinkText: {
    color: BrandColors.primary,
    marginTop: 2,
  },
  footerActions: {
    paddingHorizontal: 16,
    paddingTop: 24,
    gap: 12,
  },
  primaryButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    backgroundColor: BrandColors.primary,
    borderRadius: 14,
    paddingVertical: 16,
  },
  primaryButtonText: {
    color: BrandColors.white,
    fontSize: 15,
    fontWeight: "600",
  },
  secondaryButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    backgroundColor: "#E5E7EB",
    borderRadius: 14,
    paddingVertical: 16,
  },
  secondaryButtonText: {
    color: BrandColors.charcoal,
    fontSize: 15,
    fontWeight: "600",
  },
});
